import {ColumnAggregationType} from "components/dashboard/product-template-table/utils/enums";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Paginator} from "primereact/paginator";
import {ProgressSpinner} from "primereact/progressspinner";
import {Row} from "primereact/row";
import React, {CSSProperties, useCallback, useEffect, useId, useRef, useState} from "react";
import {Tooltip} from "react-tooltip";
import DataCell from "./components/templates/table-components/datacell";
import {AttributeHeader} from "./components/templates/table-components/header/AttributeHeader";
import {IColumnsWithValuesProps, ProductTemplateNonTransposedTableProps} from "./utils/interfaces";

import {
    checkForNumberType,
    checkForUnwantedType,
    columnsTotalsAggregation,
    getHighlightColor,
    getValueColors,
    handlePrecision
} from "./utils/utils";

const ProductTemplateTableDefault = ({
                                         templateData,
                                         configData,
                                         selectedEntryBody,
                                         selectedEntryId,
                                         selectedEntryIndex,
                                         itemsPerPage,
                                         editedDataHandler,
                                         onClickSign,
                                         handleTableKeyDown,
                                         EditRowDataCell,
                                         reportOptions,
                                         editColumnLocation = "BACK",
                                         page,
                                         setPage,
                                         allowRowEdit,
                                         foreignKeyWrap,
                                         showTotals,
                                         isTemplateDataFetching
                                     }: ProductTemplateNonTransposedTableProps) => {
    const tableId = useId().replace(/[^a-zA-Z0-9]/g, "");
    const divRef = useRef<HTMLDivElement>(null);

    const [selected, setSelected] = useState(null);
    const [rows, setRows] = useState(itemsPerPage);
    const [aggregatedColumns, setAggregatedColumns] = useState<any[]>([]);

    const rowsPerPageOptions = itemsPerPage ? [itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
            return a.indexOf(x) === i;
        }) : [];

    const handleWheel = useCallback((event: any) => {
        if (event.altKey) {
            event.preventDefault();
            const div: HTMLDivElement = event.currentTarget as HTMLDivElement;

            div.scrollLeft += event.deltaY * 0.45;
        }
    }, []);

    useEffect(() => {
        if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
            const table = document.querySelector(`#${tableId}`);
            if (table) {
                const allChildren = table.querySelectorAll("*");

                if (reportOptions.fontSize) {
                    for (let i = 0; i < allChildren.length; i++) {
                        allChildren[i].style.fontSize = reportOptions?.fontSize;
                    }
                }
            }
        }
    }, []);

    useEffect(() => {
        if (divRef.current) {
            divRef.current.addEventListener("wheel", handleWheel);
        }

        return () => {
            if (divRef.current) {
                divRef.current.removeEventListener("wheel", handleWheel);
            }
        };
    }, [divRef, handleWheel]);

    const dataCell = (val: any, index: number, configVal: any, configIndex: number) => {
        const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
        const obj = dataValue.entries[configVal.referenceName];

        let color, bgColor;
        if (obj && obj.type && checkForNumberType(obj.type)) {
            [color, bgColor] = getValueColors(obj.value, {
                highlightRanges: configVal.bgHighlightRanges,
                defaultHighlightColor: configVal.highlightColor
            });
        } else {
            [color, bgColor] = getHighlightColor(configVal.highlightColor);
        }

        return <div style={styleHandler(color, bgColor, obj && obj.type)}>
            <DataCell key={val.id}
                      headerConfig={configVal}
                      dataValue={dataValue}
                      indexHeader={configIndex}
                      indexEntry={index}
                      changeHandler={editedDataHandler}
                      isInEditMode={selectedEntryId === val.id}
                      onClickSign={onClickSign}
                      foreignKeyWrap={foreignKeyWrap}
            /></div>;
    };

    const styleHandler = (color: string | undefined, bgColor: string | undefined, type: string | undefined): CSSProperties | undefined => {
        if (checkForUnwantedType(type)) {
            if (color && bgColor) {
                return {
                    overflowX: "auto",
                    color: color,
                    backgroundColor: bgColor
                };
            }
            if (bgColor) {
                return {
                    overflowX: "auto",
                    backgroundColor: bgColor
                };
            }

            if (color) {
                return {
                    overflowX: "auto",
                    color: color
                };
            }
        }

        return {
            overflowX: "auto",
        };
    };

    const EditColumn = (frozen: boolean = false) => {
        return <Column className={"non-printable"} frozen={frozen}
                       body={(rowData, options) => EditRowDataCell(rowData, options.rowIndex)} style={{width: "0"}}
                       hidden={!allowRowEdit}/>;
    };

    const generateFooterColumns = (aggregatedColumns: IColumnsWithValuesProps[]) => {
        return aggregatedColumns.map((col) => {
            let tooltipText = "";
            if (col.aggregationType === ColumnAggregationType.SUM) {
                tooltipText = "Sum";
            } else if (col.aggregationType === ColumnAggregationType.MEAN) {
                tooltipText = "Average";
            } else if (col.aggregationType === ColumnAggregationType.COUNT) {
                tooltipText = "Count";
            }
            return (
                <Column
                    key={col.referenceName}
                    footer={
                        <div
                            data-tooltip-id="footer-total-tooltip"
                            data-tooltip-content={tooltipText}
                            data-tooltip-place="left-start"
                        >
                            {
                                col.aggregationType === ColumnAggregationType.SUM ||
                                col.aggregationType === ColumnAggregationType.MEAN
                                    ? (handlePrecision(col.total!, col.decimalPlaces!, true) + " " + col.unit)
                                    : col.aggregationType === ColumnAggregationType.COUNT
                                        ? (handlePrecision(col.total!, col.decimalPlaces!, true))
                                        : ""
                            }
                        </div>
                    }
                />
            );
        });
    };

    useEffect(() => {
        const tempColumnsWithValues: IColumnsWithValuesProps[] = [];
        configData?.data?.attributes.forEach((col) => {
            const temp: IColumnsWithValuesProps = {referenceName: "", values: []};
            temp.referenceName = col.referenceName;
            temp.aggregationType = col.aggregationType;
            temp.decimalPlaces = col.decimalPlaces;
            temp.unit = col.unit;
            temp.values = [];
            templateData.forEach((entry) => {
                if (entry.entries[col.referenceName]?.value || entry.entries[col.referenceName]?.value === 0) {
                    temp.values.push(entry.entries[col.referenceName].value);
                }
            });
            tempColumnsWithValues.push(temp);
        });
        setAggregatedColumns(columnsTotalsAggregation(tempColumnsWithValues));

    }, [configData?.data?.attributes, templateData]);


    const dynamicColumns = configData?.data?.attributes.map((col: any, i: number) => {
        return <Column key={col.referenceName}
                       field={col.referenceName}
                       className={"change-padding"}
                       header={<AttributeHeader key={col.referenceName}
                                                attribute={col}
                                                noTh={true}/>}
                       body={(rowData: any, options) => {
                           return dataCell(rowData, options.rowIndex, col, i);
                       }}
        />;
    });

    const allColumns = () => {
        const columns: JSX.Element[] = [...dynamicColumns!];

        if (editColumnLocation === "FRONT") {
            columns.unshift(EditColumn(true));
        } else if (editColumnLocation === "BACK") {
            columns.push(EditColumn(false));
        } else {
            console.error("Invalid edit column location");
        }

        return columns;
    };

    let footerGroup = null;
    if (showTotals && aggregatedColumns.length > 0) {
        footerGroup = <ColumnGroup>
            <Row>
                {generateFooterColumns(aggregatedColumns)}
            </Row>
        </ColumnGroup>;
    }


    return (
        <>
            {isTemplateDataFetching ? (
                <ProgressSpinner />
            ) : (
                <div className={"default-table"}
                     id={tableId}
                     style={{overflowX: "auto"}}
                     onKeyDown={(e) => handleTableKeyDown(e.key)}
                     ref={divRef}
                >
                    <DataTable
                        value={rows ? [...templateData.slice(page, page + rows)] : templateData}
                        size={"small"}
                        showGridlines
                        selectionMode="single"
                        selection={selected}
                        onSelectionChange={e => setSelected(e.value)}
                        footerColumnGroup={footerGroup}
                    >
                        {allColumns()}

                    </DataTable>
                </div>
            )}
            <Tooltip id="footer-total-tooltip"/>
            {itemsPerPage && !isTemplateDataFetching &&
                <Paginator totalRecords={templateData.length}
                           rows={rows}
                           first={page}
                           onPageChange={(e) => {
                               setPage(e.first);
                               setRows(e.rows);
                           }}
                           template={"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
                           currentPageReportTemplate={"Showing {first} to {last} of {totalRecords}"}
                           rowsPerPageOptions={rowsPerPageOptions}
                />
            }
        </>
    );
};

export default ProductTemplateTableDefault;